import { tr } from '@/lib/i18n-core';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ListChecks, Send, UserRound } from 'lucide-react';
import type { PreAuthCandidateDto, PreAuthRequestResultDto, PreAuthSourcesDto } from '@his/shared';
import { get, post, qs } from '@/lib/api';
import { describeError } from '@/lib/auth';
import { money } from '@/lib/utils';
import { DataTable } from '@/components/ui/layout';
import { FormActions, FormScreen, FormSection } from '@/components/ui/form-screen';
import { Alert, Button, Checkbox, Field, Input, Select, statusLabel } from '@/components/ui/primitives';
import { useToast } from '@/components/ui/toast';
import { PatientPicker } from '../patients/PatientPicker';
import { SourceStateBadge } from './insurance-status';

interface Payer {
  id: string;
  name: string;
}

interface Admission {
  id: string;
  number: string;
  status: string;
  payer?: { id: string; name: string } | null;
}

interface Picked {
  ticked: boolean;
  resendReason: string;
  askingToResend: boolean;
}

function selectableNow(line: PreAuthCandidateDto): boolean {
  return line.selectable;
}

export function PreAuthRequestPage({ returnTo, initial }: { returnTo: string; initial: { patientId?: string; payerId?: string; admissionId?: string } }) {
  const toast = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [patientId, setPatientId] = useState(initial.patientId ?? '');
  const [payerId, setPayerId] = useState(initial.payerId ?? '');
  const [admissionId, setAdmissionId] = useState(initial.admissionId ?? '');
  const [diagnosis, setDiagnosis] = useState('');
  const [picked, setPicked] = useState<Record<string, Picked>>({});
  const [result, setResult] = useState<PreAuthRequestResultDto | null>(null);
  const [error, setError] = useState<string | null>(null);

  const payers = useQuery({ queryKey: ['payers', 'branch'], queryFn: () => get<Payer[]>('/insurance/payers?availableInBranch=true') });
  const admissions = useQuery({
    queryKey: ['patient-admissions', patientId],
    queryFn: () => get<Admission[]>(`/insurance/patients/${patientId}/admissions`),
    enabled: Boolean(patientId),
  });
  const sources = useQuery({
    queryKey: ['preauth-sources', patientId, payerId, admissionId],
    queryFn: () => get<PreAuthSourcesDto>(`/integrations/nphies/pre-auth-sources${qs({ patientId, payerId, admissionId: admissionId || undefined })}`),
    enabled: Boolean(patientId && payerId),
  });

  const data = sources.data;
  const manual = data?.policy.lineSelection === 'manual';

  useEffect(() => {
    if (!data) return;
    setDiagnosis((current) => current || data.diagnosis || '');
    setPicked((current) => {
      const next: Record<string, Picked> = {};
      for (const line of data.lines) {
        next[line.sourceKey] = current[line.sourceKey] ?? { ticked: manual ? line.defaultSelected : false, resendReason: '', askingToResend: false };
      }
      return next;
    });
  }, [data, manual]);

  const groups = useMemo(() => {
    const lines = data?.lines ?? [];
    return {
      willSend: lines.filter((line) => line.defaultSelected),
      rejected: lines.filter((line) => line.state === 'rejected' && line.selectable),
    };
  }, [data]);

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['pre-auth-tab'] });
    void queryClient.invalidateQueries({ queryKey: ['pre-auth-counts'] });
    void queryClient.invalidateQueries({ queryKey: ['preauth-sources'] });
    void queryClient.invalidateQueries({ queryKey: ['pre-auths'] });
    void queryClient.invalidateQueries({ queryKey: ['pre-auth-detail'] });
    void queryClient.invalidateQueries({ queryKey: ['pre-auth-needed'] });
  };

  const submit = useMutation({
    mutationFn: (send: boolean) => {
      const allLines = data?.lines ?? [];
      const isTicked = (line: PreAuthCandidateDto) => picked[line.sourceKey]?.ticked ?? false;
      const overrides = allLines.filter((line) => isTicked(line) && picked[line.sourceKey]?.askingToResend);
      const toSelection = (line: PreAuthCandidateDto) => {
        const reason = picked[line.sourceKey]?.resendReason.trim();
        return {
          sourceType: line.sourceType,
          sourceId: line.sourceId,
          ...(line.extraQuantity > 0 ? { quantity: line.extraQuantity } : {}),
          ...(picked[line.sourceKey]?.askingToResend && reason ? { resendReason: reason } : {}),
        };
      };
      const explicit = manual ? allLines.filter(isTicked) : overrides.length > 0 ? [...allLines.filter((line) => line.defaultSelected), ...overrides] : null;
      const resubmit = manual ? [] : allLines.filter((line) => line.state === 'rejected' && isTicked(line)).map((line) => line.sourceKey);
      return post<PreAuthRequestResultDto>('/integrations/nphies/pre-auths/request', {
        patientId,
        payerId,
        ...(admissionId ? { admissionId } : {}),
        ...(diagnosis.trim() ? { diagnosis: diagnosis.trim() } : {}),
        ...(explicit ? { lines: explicit.map(toSelection) } : {}),
        resubmit,
        send,
      });
    },
    onSuccess: (outcome) => {
      setResult(outcome);
      const sent = outcome.sent;
      if (!sent) toast.success('Saved as a draft', `${outcome.lineCount} line${outcome.lineCount === 1 ? '' : 's'} are ready to send`);
      else if (sent.status === 'succeeded') toast.success('Sent to the insurer', sent.message);
      else if (sent.queued) toast.warning('Saved for later', sent.message);
      else toast.error('Could not send it', sent.message);
      refresh();
      if (!outcome.skipped.length) navigate(returnTo, { replace: true });
    },
    onError: (caught) => setError(describeError(caught)),
  });

  const ticked = (data?.lines ?? []).filter((line) => picked[line.sourceKey]?.ticked).length;
  const nothingToDo = data ? (manual ? ticked === 0 : groups.willSend.length === 0 && ticked === 0) : true;
  const ready = Boolean(patientId && payerId) && !nothingToDo && !result;
  const dirty = !submit.isSuccess && (patientId !== (initial.patientId ?? '') || payerId !== (initial.payerId ?? '') || admissionId !== (initial.admissionId ?? '') || diagnosis !== '');

  return (
    <FormScreen
      title="New pre-authorisation request"
      description="Only lines the insurer has not answered are sent. Lines already approved or waiting are never sent again."
      icon={Send}
      backTo={returnTo}
      dirty={dirty}
      wide
      onSubmit={(event) => {
        event.preventDefault();
        if (!ready) return;
        setError(null);
        submit.mutate(true);
      }}
    >
      <FormSection title={tr('billing', 'patient')} icon={UserRound}>
        <Field label="Patient" required>
          <PatientPicker
            value={patientId}
            onChange={(id) => {
              setPatientId(id);
              setAdmissionId('');
              setPicked({});
            }}
          />
        </Field>
        <Field label="Payer" required>
          <Select
            value={payerId}
            onChange={(event) => {
              setPayerId(event.target.value);
              setPicked({});
            }}
          >
            <option value="">Choose…</option>
            {payers.data?.map((payer) => (
              <option key={payer.id} value={payer.id}>
                {payer.name}
              </option>
            ))}
          </Select>
        </Field>
        {patientId && (admissions.data?.length ?? 0) > 0 && (
          <Field label="Admission" hint="Leave empty for an outpatient visit">
            <Select
              value={admissionId}
              onChange={(event) => {
                setAdmissionId(event.target.value);
                setPicked({});
              }}
            >
              <option value="">Not an admission</option>
              {admissions.data?.map((admission) => (
                <option key={admission.id} value={admission.id}>
                  {admission.number} · {statusLabel(admission.status, 'admission_status')}
                </option>
              ))}
            </Select>
          </Field>
        )}
        <Field label="Diagnosis" hint="ICD-10 code and description, for example J18.9 - Pneumonia">
          <Input value={diagnosis} onChange={(event) => setDiagnosis(event.target.value)} />
        </Field>
      </FormSection>

      <FormSection title={tr('billing', 'billed_lines')} icon={ListChecks} columns={1}>
        {data && (
          <Alert>
            {manual
              ? 'Tick the lines to send. Lines that already have an answer are locked.'
              : 'Every line that has no answer yet will be sent. Nothing to tick. A rejected line goes again only if you tick “Send again”.'}
            {' Save a draft to check it first, or send it now.'}
          </Alert>
        )}

        {sources.isFetching && !data && <p className="text-sm text-muted-foreground">Looking for the patient’s billed lines…</p>}
        {!patientId || !payerId ? <p className="text-sm text-muted-foreground">Choose the patient and the payer to see their billed lines.</p> : null}
        {sources.error && <Alert variant="destructive">{describeError(sources.error)}</Alert>}

        {data && (
          <DataTable
            dense
            rows={data.lines}
            rowKey={(row) => row.sourceKey}
            empty="This patient has no billed lines for this payer yet. Add the services on a bill first."
            columns={[
              {
                header: manual ? 'Send' : 'Again',
                width: '70px',
                cell: (row) => {
                  const pick = picked[row.sourceKey];
                  const canTick = manual ? selectableNow(row) : row.state === 'rejected' && row.selectable;
                  if (!canTick) return <span className="text-muted-foreground">{row.locked ? 'locked' : '—'}</span>;
                  return (
                    <Checkbox
                      aria-label={`Send ${row.description}`}
                      checked={pick?.ticked ?? false}
                      onCheckedChange={(checked) => setPicked({ ...picked, [row.sourceKey]: { ticked: checked, resendReason: pick?.resendReason ?? '', askingToResend: false } })}
                    />
                  );
                },
              },
              { header: 'Service', cell: (row) => <div><p>{row.description}</p><p className="text-xs text-muted-foreground">{row.invoiceNumber}{row.code ? ` · ${row.code}` : ''}</p></div> },
              { header: 'Qty', align: 'right', cell: (row) => (row.extraQuantity > 0 ? `+${row.extraQuantity}` : row.quantity) },
              { header: 'Amount', align: 'right', cell: (row) => money(row.amountMinor) },
              { header: 'Insurer', cell: (row) => <SourceStateBadge state={row.state} /> },
              {
                header: '',
                cell: (row) => {
                  const pick = picked[row.sourceKey];
                  const answered = ['approved', 'partial', 'pending', 'pended', 'not_required'].includes(row.state) && row.extraQuantity === 0;
                  return (
                    <div className="space-y-1">
                      {row.note && <p className="text-xs text-muted-foreground">{row.note}</p>}
                      {answered && data.policy.allowResendAnswered && data.canOverride && (
                        <div className="space-y-1">
                          <Checkbox
                            checked={pick?.askingToResend ?? false}
                            onCheckedChange={(checked) => setPicked({ ...picked, [row.sourceKey]: { ticked: checked, resendReason: pick?.resendReason ?? '', askingToResend: checked } })}
                            label="Send it again anyway"
                          />
                          {pick?.askingToResend && (
                            <Input
                              placeholder="Why? (kept in the audit trail)"
                              value={pick.resendReason}
                              onChange={(event) => setPicked({ ...picked, [row.sourceKey]: { ...pick, resendReason: event.target.value } })}
                            />
                          )}
                        </div>
                      )}
                    </div>
                  );
                },
              },
            ]}
          />
        )}

        {result && result.skipped.length > 0 && (
          <Alert variant="warning" title="Left out of this request">
            <ul className="mt-1 list-disc ps-5">
              {result.skipped.map((line) => (
                <li key={line.sourceKey}>
                  {line.display}: {line.reason}
                </li>
              ))}
            </ul>
          </Alert>
        )}
        {error && <Alert variant="destructive">{error}</Alert>}
      </FormSection>

      <FormActions
        submitLabel="Send now"
        cancelLabel={result ? 'Close' : undefined}
        loading={submit.isPending}
        incomplete={!ready}
        cancelTo={returnTo}
        secondary={
          !result ? (
            <Button
              type="button"
              variant="outline"
              loading={submit.isPending}
              disabled={!ready}
              onClick={() => {
                setError(null);
                submit.mutate(false);
              }}
            >
              Save as a draft
            </Button>
          ) : undefined
        }
      />
    </FormScreen>
  );
}
